import _extends from "@babel/runtime/helpers/extends";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AuthoritiesEnum, IntegrationTypeEnum, ThemeTypeEnum } from "~/types/enums";
import { Alert, Badge, Box, Button, Card, Grid, Icon, InputSelect, InputToggle, Link, Modal, Picture, Spinner, Text } from "~/components-shared";
import integrationsActionCreators from "~/store/integrations/actions";
import integrationSelectors from "~/store/integrations/selectors";
import slack from "~/assets/i/apps/slack_logo.svg";
import useAbacExt from "~/hooks/abac";
import api from "~/lib/api";
import { useTranslation } from "react-i18next";
import styled, { useTheme } from "styled-components";
import { getSlackSpaceUrl, handleError, helpers } from "~/lib/helpers";
import toastsActionCreators from "~/store/toasts/actions";
import zendeskLogoDark from "~/assets/i/apps/zendesk_logo_dark.svg";
import zendeskLogoLight from "~/assets/i/apps/zendesk_logo_white.svg";
import gongLogo from "~/assets/i/apps/gong_logo.svg";
import joynLogo from "~/assets/i/logo.png";
import Markdown from "~/components/Markdown";
const Actions = styled.div`
  position: relative;
  z-index: 2;
  justify-self: end;
`;
const OrderedList = styled.ol`
  list-style: decimal;
  margin: 0 2em 0 3em;
`;
const OrderedListItem = styled.li`
  margin-left: 1em;
  margin-bottom: 1.5em;
  list-style-type: decimal;
`;
const appIntegrations = theme => [{
  id: 1,
  name: "Zendesk",
  logo: theme.type === ThemeTypeEnum.LIGHT ? zendeskLogoDark : zendeskLogoLight,
  showInfo: true,
  steps: ["Login to **[Zendesk](https://www.zendesk.com/login/)**", "Navigate to your **[Admin Center](https://support.zendesk.com/hc/en-us/articles/4581766374554#topic_hfg_dyz_1hb)**", "Follow **[these steps](https://support.zendesk.com/hc/en-us/articles/4963959597594-Creating-and-managing-ticket-triggers-for-Slack?page=1&sort_by=created_at&sort_order=desc)** to setup a Slack trigger", "Once setup, invite **@joyn** to the channel"]
}, {
  id: 2,
  name: "Gong",
  logo: gongLogo,
  showInfo: true,
  steps: ["Login to **[Gong](https://app.gong.io/welcome/sign-in)**", "Go to **[Settings > Slack](https://www.gong.io/collective/integrations/slack)**", "Click CONNECT and add permissions", "Once setup, invite **@joyn** to the channel"]
}];
const recommendedChannel = name => {
  const terms = ["dev", "eng", "prod", "product", "engineering", "develop", "customer", "feedback", "release", "ship"];
  return terms.some(term => name.includes(term));
};
const mapChannelToOption = channel => ({
  id: channel.id,
  pre: channel.isMember ? " Joyn is present" : " No Joyn",
  value: `${channel.isMpim ? channel.purpose : `#${channel.name}`}${channel.isPrivate ? " (private)" : ""}`,
  post: helpers.mapSlackChannelTypes(channel),
  disabled: channel.isMember,
  selected: channel.isMember
});
const authorities = [AuthoritiesEnum.INTEGRATIONS_WRITE, AuthoritiesEnum.ONBOARDING_COMPANY_INTEGRATIONS_WRITE, AuthoritiesEnum.SIGN_UP_INTEGRATE];
const SlackIntegrationCard = _ref => {
  let {
    ...rest
  } = _ref;
  const theme = useTheme();
  const {
    t
  } = useTranslation();
  const {
    userHasOneOf
  } = useAbacExt();
  const dispatch = useDispatch();
  const slackIntegration = useSelector(integrationSelectors.getIntegrationByType(IntegrationTypeEnum.SLACK));
  const [isExpanded, setIsExpanded] = useState(false);
  const [channelsLoading, setChannelsLoading] = useState(false);
  const [addingLoading, setAddingLoading] = useState(false);
  const [selectedApp, setSelectedApp] = useState(undefined);
  const selectedAppInfo = appIntegrations(theme).find(item => item.id === selectedApp);
  const [selectedChannels, setSelectedChannels] = useState([]);
  const [channels, setChannels] = useState([]);
  const [channelsError, setChannelsError] = useState();
  const [addingError, setAddingError] = useState();
  const error = channelsError || addingError;
  const recommendedChannels = channels.filter(channel => !selectedChannels.includes(channel.id) && !channel.isMember && recommendedChannel(channel.name)).slice(0, 10);
  const fetchSlackChannels = () => {
    setChannelsLoading(true);
    api.fetchSlackChannels().then(response => {
      setChannels(response.filter(channel => !channel.isPrivate));
    }).catch(e => setChannelsError(handleError(e))).finally(() => {
      setChannelsLoading(false);
    });
  };
  useEffect(() => {
    if (slackIntegration) {
      fetchSlackChannels();
    }
  }, [!!slackIntegration]);
  const handleToggle = value => {
    if (value) {
      api.slackInstall().then(_ref2 => {
        let {
          path
        } = _ref2;
        const width = 560;
        const height = 700;
        const left = window.screen.width / 2 - width / 2;
        const top = window.screen.height / 2 - height / 2;
        window.open(path, "Install Slack", `width=${width},height=${height},left=${left},top=${top}`);
      });
      setIsExpanded(true);
    } else if (slackIntegration?.id) {
      dispatch(integrationsActionCreators.deleteStarted(slackIntegration.id));
    }
  };
  const handleChannelClick = id => {
    if (selectedChannels.includes(id)) {
      setSelectedChannels(prev => prev.filter(p => p !== id));
    } else {
      setSelectedChannels(prev => [...prev, id]);
    }
  };
  const handleAddChannels = () => {
    setAddingLoading(true);
    api.addBotToSlackChannels(selectedChannels).then(() => {
      dispatch(toastsActionCreators.pushed({
        severity: "success",
        message: t("SlackIntegrationCard.botAddedToChannels", {
          count: selectedChannels.length
        }),
        timeout: 10000
      }));
      setSelectedChannels([]);
      fetchSlackChannels();
    }).catch(e => setAddingError(handleError(e))).finally(() => {
      setAddingLoading(false);
    });
  };
  return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Card, _extends({
    onClick: userHasOneOf(authorities) && slackIntegration ? () => setIsExpanded(prev => !prev) : undefined,
    title: /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Grid, {
      cols: 4,
      template: "auto auto 1fr auto",
      align: "center",
      s: {
        mb: 3
      },
      fullWidth: true
    }, /*#__PURE__*/React.createElement(Picture, {
      src: slack,
      width: "30px",
      height: "30px",
      border: "squared",
      color: "transparent",
      s: {
        mr: 2
      }
    }), /*#__PURE__*/React.createElement(Text, {
      element: "h3",
      display: "inline-block"
    }, "Slack"), slackIntegration && userHasOneOf(authorities) && /*#__PURE__*/React.createElement(Icon, {
      scale: 4,
      src: "chevron",
      rotate: isExpanded ? 0 : 180,
      s: {
        ml: 1,
        mb: 1
      }
    }), /*#__PURE__*/React.createElement(Actions, null, /*#__PURE__*/React.createElement(InputToggle, {
      display: "inline-block",
      value: !!slackIntegration,
      onChange: (value, event) => {
        event.stopPropagation();
        handleToggle(value);
      },
      disabled: !userHasOneOf(authorities)
    }))), /*#__PURE__*/React.createElement(Box, {
      gap: 2,
      direction: "row"
    }, appIntegrations(theme).map(app => /*#__PURE__*/React.createElement(Button, {
      onClick: app.showInfo ? event => {
        event.stopPropagation();
        setSelectedApp(app.id);
      } : undefined,
      elemBefore: /*#__PURE__*/React.createElement(Picture, {
        width: "1.2rem",
        height: "1.2rem",
        src: app.logo,
        display: "block",
        backgroundSize: "contain",
        border: "squared",
        s: {
          mr: 1
        }
      }),
      s: {
        pl: 2,
        pr: app.showInfo ? 1.5 : 3,
        py: 1
      },
      fontSize: 3.5,
      color: "translucent"
    }, app.name, app.showInfo && /*#__PURE__*/React.createElement(Icon, {
      src: "infoOutline",
      color: "primary",
      s: {
        ml: 1,
        mt: -0.5
      }
    }))))),
    collapsed: !slackIntegration || !isExpanded,
    s: {
      mb: 3
    }
  }, rest), slackIntegration && /*#__PURE__*/React.createElement(Box, {
    onClick: e => e.stopPropagation(),
    fullWidth: true
  }, error && /*#__PURE__*/React.createElement(Alert, {
    severity: "danger",
    s: {
      mb: 3
    },
    fullWidth: true
  }, error), /*#__PURE__*/React.createElement(Text, {
    display: "block",
    fontSize: 4,
    lineHeight: 6,
    s: {
      mb: 3
    }
  }, t("CompanyIntegrations.connectedTo"), " ", /*#__PURE__*/React.createElement(Link, {
    color: "primary",
    href: getSlackSpaceUrl(slackIntegration),
    underline: true
  }, slackIntegration.token.team?.name), " ", t("CompanyIntegrations.workspace"), ". Select the channel(s) you want us to summarize and map:"), channelsLoading ? /*#__PURE__*/React.createElement(Spinner, {
    s: {
      mt: 6
    },
    center: true
  }) : /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    s: {
      mb: 4
    },
    fullWidth: true
  }, !!recommendedChannels.length && /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(Text, {
    weight: "semiBold",
    fontSize: 4,
    display: "block",
    s: {
      mb: 2
    }
  }, "Recommended by Joyn"), /*#__PURE__*/React.createElement(Box, {
    gap: 1,
    s: {
      mb: 5
    }
  }, recommendedChannels.map(channel => /*#__PURE__*/React.createElement(Button, {
    key: channel.id,
    onClick: () => handleChannelClick(channel.id),
    color: selectedChannels.includes(channel.id) ? "primary" : "translucent",
    fontSize: 3,
    s: {
      px: 2,
      py: 0.5
    },
    outline: false,
    rounded: true
  }, mapChannelToOption(channel).value)))), /*#__PURE__*/React.createElement(InputSelect, {
    value: selectedChannels,
    onChange: value => setSelectedChannels(value),
    options: channels.sort((a, b) => a.isMember === b.isMember ? 0 : a.isMember ? -1 : 1).map(channel => mapChannelToOption(channel)),
    placeholder: "Select channels",
    multiple: true,
    withSearch: true,
    fullSelections: true,
    fullWidth: true
  })), /*#__PURE__*/React.createElement(Box, {
    gap: 3,
    fullWidth: true,
    justify: "end"
  }, !!selectedChannels.length && /*#__PURE__*/React.createElement(Button, {
    color: "transparent",
    onClick: () => setSelectedChannels([]),
    fontSize: 3.5,
    s: {
      px: 4,
      py: 2
    },
    outline: false
  }, "Clear selection"), /*#__PURE__*/React.createElement(Button, {
    loading: addingLoading,
    onClick: handleAddChannels,
    disabled: !selectedChannels.length,
    fontSize: 3.5,
    s: {
      px: 4,
      py: 2
    }
  }, /*#__PURE__*/React.createElement(Icon, {
    src: "plus",
    s: {
      mt: -0.5,
      mr: 1
    }
  }), "Add Joyn to channels")))), /*#__PURE__*/React.createElement(Modal, {
    open: !!selectedAppInfo
  }, /*#__PURE__*/React.createElement(Modal.Body, {
    onClose: () => setSelectedApp(undefined),
    maxWidth: "480px",
    fullWidth: true
  }, /*#__PURE__*/React.createElement(Modal.Content, null, /*#__PURE__*/React.createElement(Box, {
    direction: "column",
    align: "center"
  }, /*#__PURE__*/React.createElement(Box, {
    direction: "row",
    s: {
      mb: 5
    },
    align: "center",
    gap: 2
  }, /*#__PURE__*/React.createElement(Badge, {
    color: "translucent",
    s: {
      p: 3
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    width: "4rem",
    height: "4rem",
    backgroundSize: "cover",
    src: selectedAppInfo?.logo,
    color: "grey"
  })), /*#__PURE__*/React.createElement(Icon, {
    src: "sync",
    scale: 6,
    color: "textSecondary"
  }), /*#__PURE__*/React.createElement(Badge, {
    color: "primary",
    s: {
      p: 3
    }
  }, /*#__PURE__*/React.createElement(Picture, {
    width: "4rem",
    height: "4rem",
    backgroundSize: "cover",
    src: joynLogo,
    color: "primary"
  }))), /*#__PURE__*/React.createElement(Text, {
    element: "h3",
    align: "center",
    fontSize: 6,
    weight: "semiBold",
    display: "block"
  }, selectedAppInfo?.name), /*#__PURE__*/React.createElement(Text, {
    align: "center",
    fontSize: 3,
    color: "textSecondary",
    weight: "semiBold",
    s: {
      mb: 7
    },
    display: "block"
  }, "via Slack"), /*#__PURE__*/React.createElement(Markdown, {
    fontSize: 4,
    lineHeight: 6,
    align: "center",
    s: {
      mb: 8
    },
    color: "textSecondary",
    text: `If you already have a **${selectedAppInfo?.name}-Slack** channel setup – simply invite **@joyn** to the channel`
  }), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    s: {
      mb: 5
    }
  }, "Not connected yet? Let's go through it together:"), /*#__PURE__*/React.createElement(Text, {
    fontSize: 4,
    lineHeight: 6
  }, /*#__PURE__*/React.createElement(OrderedList, null, selectedAppInfo?.steps.map(step => /*#__PURE__*/React.createElement(OrderedListItem, null, /*#__PURE__*/React.createElement(Markdown, {
    text: step
  }))))))), /*#__PURE__*/React.createElement(Modal.Actions, null, /*#__PURE__*/React.createElement(Button, {
    color: "translucent",
    s: {
      px: 8
    },
    onClick: () => setSelectedApp(undefined),
    rounded: true
  }, t("common.actions.close"))))));
};
export default SlackIntegrationCard;